import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  useGetBanquetSpecialDatesMutation,
  useSaveBanquetSpecialDateMutation,
  useDeleteBanquetSpecialDateMutation,
} from "../../../../Api/attoDeskApi";
import { useNotifier } from "../../../../Core/Notifier";
import { useStyles } from "../../../../theme/appStyles";
import { HALL_OPTIONS, ALL_HALLS } from "../constants";

// ── Types ──────────────────────────────────────────────────────────────────────
interface SpecialDateRow {
  id:          number;
  dateStart:   string;
  dateEnd:     string;
  label:       string;
  hallKey:     string;
  rateAM:      number | null;
  ratePM:      number | null;
  rateFullDay: number | null;
}

// ── Label presets ──────────────────────────────────────────────────────────────
const PRESET_LABELS = [
  "Public Holiday",
  "Peak Wedding Season",
  "Long Weekend",
  "Victoria Day",
  "Canada Day",
  "Labour Day",
  "Thanksgiving",
  "Christmas Eve",
  "Christmas Day",
  "New Year's Eve",
  "New Year's Day",
  "Good Friday",
  "Easter Sunday",
  "Diwali",
  "Eid",
  "Custom…",
] as const;

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmtRate = (val: number | null | undefined): string =>
  val !== null && val !== undefined ? `$${Number(val).toFixed(2)}` : "—";

const fmtDateRange = (start: string, end: string): string =>
  start === end ? start : `${start} → ${end}`;

// Map a stored label back to its preset (or "Custom…")
const labelToPreset = (label: string): string =>
  (PRESET_LABELS as readonly string[]).includes(label) ? label : "Custom…";

interface FormState {
  id:          number | undefined;
  isRange:     boolean;
  dateStart:   string;
  dateEnd:     string;
  labelPreset: string;
  customLabel: string;
  hallKey:     string;
  rateAM:      string;
  ratePM:      string;
  rateFullDay: string;
}

const EMPTY_FORM: FormState = {
  id:          undefined,
  isRange:     false,
  dateStart:   "",
  dateEnd:     "",
  labelPreset: "Public Holiday",
  customLabel: "",
  hallKey:     ALL_HALLS,
  rateAM:      "",
  ratePM:      "",
  rateFullDay: "",
};

// ── Component ──────────────────────────────────────────────────────────────────
const SpecialDatesTab = () => {
  const theme   = useTheme();
  const classes = useStyles();
  const { showMessage, showErrorMessage } = useNotifier();

  const [rows,       setRows]       = useState<SpecialDateRow[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [deleteId,   setDeleteId]   = useState<number | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  // ── Inline form state ─────────────────────────────────────────────────────
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  // ── List filters ──────────────────────────────────────────────────────────
  const [search,     setSearch]     = useState("");
  const [yearFilter, setYearFilter] = useState("all");

  const [getSpecialDates]                            = useGetBanquetSpecialDatesMutation();
  const [saveSpecialDate, { isLoading: isSaving }]   = useSaveBanquetSpecialDateMutation();
  const [deleteSpecialDate, { isLoading: isDeleting }] = useDeleteBanquetSpecialDateMutation();

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchDates = useCallback(async () => {
    setIsFetching(true);
    try {
      const res = (await getSpecialDates({}).unwrap()) as any;
      if (res.status && Array.isArray(res.data)) {
        setRows(res.data);
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "Failed to load special dates");
    } finally {
      setIsFetching(false);
    }
  }, [getSpecialDates, showErrorMessage]);

  useEffect(() => { fetchDates(); }, [fetchDates]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const errs: Partial<Record<keyof FormState, string>> = {};
    const effectiveLabel =
      form.labelPreset === "Custom…" ? form.customLabel.trim() : form.labelPreset;
    if (!effectiveLabel) errs.customLabel = "Label is required";
    if (!form.dateStart) errs.dateStart = "Date is required";
    if (form.isRange) {
      if (!form.dateEnd) errs.dateEnd = "End date is required";
      else if (form.dateEnd < form.dateStart) errs.dateEnd = "End must be on or after start";
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return;

    const effectiveLabel =
      form.labelPreset === "Custom…" ? form.customLabel.trim() : form.labelPreset;
    const dateEnd = form.isRange ? form.dateEnd : form.dateStart;

    const payload: any = {
      dateStart:   form.dateStart,
      dateEnd,
      label:       effectiveLabel,
      hallKey:     form.hallKey || ALL_HALLS,
      rateAM:      form.rateAM      !== "" ? Number(form.rateAM)      : undefined,
      ratePM:      form.ratePM      !== "" ? Number(form.ratePM)      : undefined,
      rateFullDay: form.rateFullDay !== "" ? Number(form.rateFullDay) : undefined,
    };
    if (form.id !== undefined) payload.id = form.id;

    try {
      const res = (await saveSpecialDate(payload).unwrap()) as any;
      if (res.status) {
        showMessage(res.message ?? "Special date saved");
        setForm(EMPTY_FORM);
        setFormErrors({});
        fetchDates();
      } else {
        showErrorMessage(res.message ?? "Failed to save");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? err?.message ?? "Something went wrong");
    }
  };

  const handleClear = () => {
    setForm(EMPTY_FORM);
    setFormErrors({});
  };

  // ── Load row into form for editing ────────────────────────────────────────
  const openEdit = (row: SpecialDateRow) => {
    const isRange = row.dateStart !== row.dateEnd;
    const preset  = labelToPreset(row.label);
    setForm({
      id:          row.id,
      isRange,
      dateStart:   row.dateStart,
      dateEnd:     row.dateEnd,
      labelPreset: preset,
      customLabel: preset === "Custom…" ? row.label : "",
      hallKey:     row.hallKey ?? ALL_HALLS,
      rateAM:      row.rateAM      !== null ? String(row.rateAM)      : "",
      ratePM:      row.ratePM      !== null ? String(row.ratePM)      : "",
      rateFullDay: row.rateFullDay !== null ? String(row.rateFullDay) : "",
    });
    setFormErrors({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleConfirmDelete = async () => {
    if (deleteId === null) return;
    try {
      const res = (await deleteSpecialDate(deleteId).unwrap()) as any;
      if (res.status) {
        showMessage("Special date rule deleted");
        if (form.id === deleteId) setForm(EMPTY_FORM);
        fetchDates();
      } else {
        showErrorMessage(res.message ?? "Failed to delete");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "Something went wrong");
    } finally {
      setDeleteId(null);
    }
  };

  // ── Filtered list ─────────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => {
      const matchSearch =
        !q ||
        r.label.toLowerCase().includes(q) ||
        r.dateStart.includes(q) ||
        r.dateEnd.includes(q);
      const matchYear =
        yearFilter === "all" || r.dateStart.startsWith(yearFilter);
      return matchSearch && matchYear;
    });
  }, [rows, search, yearFilter]);

  // ── Hint message for form ─────────────────────────────────────────────────
  const formHint = useMemo(() => {
    const parts: string[] = [];
    const effectiveLabel =
      form.labelPreset === "Custom…" ? form.customLabel.trim() : form.labelPreset;
    if (effectiveLabel) parts.push(effectiveLabel);
    if (form.dateStart) {
      const range = form.isRange && form.dateEnd
        ? `${form.dateStart} → ${form.dateEnd}`
        : form.dateStart;
      parts.push(range);
    }
    if (!parts.length) return "";
    return parts.join(" · ");
  }, [form.labelPreset, form.customLabel, form.dateStart, form.dateEnd, form.isRange]);

  const inputSx = { "& .MuiInputBase-root": { fontSize: 13 } };
  const cellSx  = { py: "8px", px: "10px" };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>

      {/* ── Inline form card ── */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
        {/* Form header */}
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: "action.hover" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {form.id !== undefined ? "Edit special date rule" : "Add / update a special date rule"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Single date or a date range — rates override the base matrix for any booking that falls within
          </Typography>
        </Box>

        <Box sx={{ p: 2.5 }}>

          {/* Entry type toggle */}
          <Box sx={{ display: "flex", gap: 1, mb: 2.5, alignItems: "center" }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 11 }}>
              Entry type:
            </Typography>
            <Button
              size="small"
              variant={!form.isRange ? "contained" : "outlined"}
              onClick={() => setField("isRange", false)}
              sx={{ borderRadius: "20px", fontSize: 12, px: 2 }}
            >
              Single date
            </Button>
            <Button
              size="small"
              variant={form.isRange ? "contained" : "outlined"}
              onClick={() => setField("isRange", true)}
              sx={{ borderRadius: "20px", fontSize: 12, px: 2 }}
            >
              Date range
            </Button>
          </Box>

          {/* Date + label + hall row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: form.isRange ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            {/* Single date */}
            {!form.isRange && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                  Date *
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.dateStart}
                  onChange={(e) => setField("dateStart", e.target.value)}
                  error={!!formErrors.dateStart}
                  helperText={formErrors.dateStart}
                  sx={inputSx}
                />
              </Box>
            )}

            {/* Range: from + to */}
            {form.isRange && (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                    From date *
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={form.dateStart}
                    onChange={(e) => setField("dateStart", e.target.value)}
                    error={!!formErrors.dateStart}
                    helperText={formErrors.dateStart}
                    sx={inputSx}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                    To date *
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={form.dateEnd}
                    onChange={(e) => setField("dateEnd", e.target.value)}
                    inputProps={{ min: form.dateStart || undefined }}
                    error={!!formErrors.dateEnd}
                    helperText={formErrors.dateEnd}
                    sx={inputSx}
                  />
                </Box>
              </>
            )}

            {/* Label */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                Label / reason *
              </Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={form.labelPreset}
                onChange={(e) => setField("labelPreset", e.target.value)}
                sx={inputSx}
              >
                {PRESET_LABELS.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Custom label */}
            {form.labelPreset === "Custom…" && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                  Custom label *
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="e.g. Tamil New Year"
                  value={form.customLabel}
                  onChange={(e) => setField("customLabel", e.target.value)}
                  error={!!formErrors.customLabel}
                  helperText={formErrors.customLabel}
                  sx={inputSx}
                />
              </Box>
            )}

            {/* Hall */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                Applies to hall
              </Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={form.hallKey}
                onChange={(e) => setField("hallKey", e.target.value)}
                sx={inputSx}
              >
                <MenuItem value={ALL_HALLS}>All halls &amp; combos</MenuItem>
                <Divider />
                {HALL_OPTIONS.map((h) => (
                  <MenuItem key={h.key} value={h.key}>{h.key} ({h.capacity} pax)</MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          {/* Rate inputs */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2, mb: formHint ? 1.5 : 0 }}>
            {[
              { label: "AM rate ($/person)",       field: "rateAM"      as const },
              { label: "PM rate ($/person)",        field: "ratePM"      as const },
              { label: "Full Day rate ($/person)",  field: "rateFullDay" as const },
            ].map(({ label, field }) => (
              <Box key={field}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                  {label}
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  placeholder="blank = base rate"
                  value={form[field]}
                  onChange={(e) => setField(field, e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="caption" color="text.secondary">$</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              </Box>
            ))}
          </Box>

          {/* Hint */}
          {formHint && (
            <Alert severity="info" sx={{ mt: 1.5, fontSize: 12.5, py: 0.5 }}>
              {formHint}
            </Alert>
          )}
        </Box>

        {/* Form footer */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: "action.hover",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Blank fields inherit the base rate. Date range rules apply to every date within the range.
            Special date rates always take priority.
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" variant="outlined" className={classes.addButton} onClick={handleClear}>
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              className={classes.addButton}
              onClick={handleSave}
              disabled={isSaving}
              startIcon={isSaving ? undefined : <SaveAltIcon />}
            >
              {isSaving ? "Saving…" : form.id !== undefined ? "Update rule" : "Save rule"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* ── List header ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Configured special dates
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search dates or labels…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 210, ...inputSx }}
          />
          <TextField
            select
            size="small"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            sx={{ width: 100, ...inputSx }}
          >
            <MenuItem value="all">All years</MenuItem>
            <MenuItem value="2025">2025</MenuItem>
            <MenuItem value="2026">2026</MenuItem>
            <MenuItem value="2027">2027</MenuItem>
          </TextField>
        </Box>
      </Box>

      {isFetching && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      {/* ── List table ── */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflowX: "auto" }}
      >
        <Table size="small" sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: "action.hover",
                "& th": { fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", ...cellSx },
              }}
            >
              <TableCell sx={{ minWidth: 150 }}>Label</TableCell>
              <TableCell sx={{ minWidth: 190 }}>Date / Range</TableCell>
              <TableCell sx={{ minWidth: 170 }}>Applies to Hall</TableCell>
              <TableCell sx={{ minWidth: 100 }}>AM Rate</TableCell>
              <TableCell sx={{ minWidth: 100 }}>PM Rate</TableCell>
              <TableCell sx={{ minWidth: 110 }}>Full Day Rate</TableCell>
              <TableCell align="center" sx={{ width: 88 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 && !isFetching && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5, color: "text.secondary", fontSize: 14 }}>
                  {rows.length === 0 ? "No special date rules. Use the form above to add one." : "No results match the current filters."}
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  "&:hover": { bgcolor: "action.hover" },
                  ...(form.id === row.id ? { bgcolor: "primary.50", "& td": { borderLeft: "3px solid", borderLeftColor: "primary.main" } } : {}),
                }}
              >
                <TableCell sx={cellSx}>
                  <Typography variant="body2" fontWeight={600}>{row.label}</Typography>
                </TableCell>
                <TableCell sx={cellSx}>
                  <Typography variant="body2">{fmtDateRange(row.dateStart, row.dateEnd)}</Typography>
                </TableCell>
                <TableCell sx={cellSx}>
                  <Chip
                    label={row.hallKey === ALL_HALLS || !row.hallKey ? "All Halls" : row.hallKey}
                    size="small"
                    variant="outlined"
                    color={row.hallKey === ALL_HALLS || !row.hallKey ? "primary" : "default"}
                    sx={{ fontSize: 12 }}
                  />
                </TableCell>
                <TableCell sx={cellSx}><Typography variant="body2">{fmtRate(row.rateAM)}</Typography></TableCell>
                <TableCell sx={cellSx}><Typography variant="body2">{fmtRate(row.ratePM)}</Typography></TableCell>
                <TableCell sx={cellSx}><Typography variant="body2">{fmtRate(row.rateFullDay)}</Typography></TableCell>
                <TableCell align="center" sx={cellSx}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(row)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => setDeleteId(row.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Delete confirmation ── */}
      <Dialog
        open={deleteId !== null}
        onClose={() => setDeleteId(null)}
        maxWidth="xs"
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberIcon color="error" />
            Delete Special Date?
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently remove this special date rule. Future bookings will fall back to base pricing.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" className={classes.addButton} onClick={() => setDeleteId(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            className={classes.addButton}
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpecialDatesTab;
